import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
  DOCUMENT,
} from "@angular/core";

import { FormioModule, FormioForm, FormioAppConfig, FormioComponent } from "@formio/angular";
import { forEach } from "lodash-es";
import { FormIoEventResponse } from "../models";
@Component({
  selector: "rr-ui-core-form-io-manager",
  imports: [FormioModule],
  templateUrl: "./ui-core-form-io-manager.component.html",
  styleUrl: "./ui-core-form-io-manager.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FormioAppConfig,
      useValue: {
        icons: "bi",
      },
    },
  ],
})
export class UiCoreFormIoManagerComponent implements OnInit, AfterViewInit, OnDestroy {
  // DI
  readonly #renderer = inject(Renderer2);
  readonly #document = inject(DOCUMENT);

  // I/O
  id = input<string>();
  form = input.required<FormioForm>();
  data = input.required<object | null>();
  orgName = input.required<string | null>();
  title = input<string | null>();
  canInitialSave = input<boolean | null>();
  isCompleted = input<boolean>();
  showUpload = output<string | null>();
  next = output<FormIoEventResponse>();
  save = output<object | null>();
  submitForm = output<object | null>();
  previewReport = output<void>();
  downloadReport = output<void>();
  backToHome = output<void>();
  ready = output<FormioComponent | null>();
  autoSaveBeforeUpload = output<object | null>();

  // View
  formIo = viewChild<FormioComponent>("formIo");
  formIoEl = computed(() => (this.formIo() as { formioElement: ElementRef })?.formioElement);
  submissonEventData = signal<object | null>(null);
  isLoading = signal(true);

  submitted = signal(false);
  readonly #listeners: (() => void)[] = [];

  constructor() {
    effect(() => {
      if (this.submissonEventData() && this.canInitialSave()) {
        this.formIo()?.formio.nextPage();
      }
    });
  }

  ngOnInit(): void {
    this.loadStyle("form-bootstrap-icons");
  }

  ngAfterViewInit(): void {
    this.ready.emit(this.formIo() ?? null);
  }

  ngOnDestroy(): void {
    for (const fn of this.#listeners) {
      fn();
    }
    this.removeStyle("form-bootstrap-icons");
  }

  private loadStyle(name: string) {
    const link = this.#renderer.createElement("link");
    link.rel = "stylesheet";
    link.href = `${name}.css`;
    link.id = `${name}-css`;
    this.#renderer.appendChild(this.#document.head, link);
  }

  private removeStyle(id: string) {
    const fontAwesomeSource = this.#document.getElementById(`${id}-css`);
    if (fontAwesomeSource) {
      fontAwesomeSource.remove();
    }
  }

  private prepareFormActions() {
    // Move actions at the bottom of questions
    const parent = this.formIoEl().nativeElement.querySelector(".wizard-page");
    if (parent) {
      const actions = this.formIoEl().nativeElement.querySelector(".formio-wizard-nav-container");
      this.#renderer.appendChild(parent, actions);
    }

    // Create save button if not exists
    const actionWrapper = this.formIoEl().nativeElement.querySelector(".formio-wizard-nav-container");
    let actionSave = actionWrapper?.querySelector(".btn.form-btn-save");

    if (!actionSave) {
      actionSave = this.#renderer.createElement("button");
      this.#renderer.setProperty(actionSave, "classList", [
        ...actionSave.classList,
        "btn btn-primary form-manager-action form-btn-save me-2",
      ]);
      this.#renderer.setProperty(actionSave, "innerText", "Save");
      this.#listeners.push(this.#renderer.listen(actionSave, "click", () => this.onSave()));
    }

    const actionPrev = actionWrapper?.querySelector(".btn.btn-wizard-nav-previous");
    const actionNext = actionWrapper?.querySelector(".btn.btn-wizard-nav-next");
    if (actionPrev) {
      if (actionNext) {
        this.#renderer.insertBefore(actionNext.parentElement, actionSave, actionNext);
      }
    } else {
      this.#renderer.addClass(actionWrapper, "justify-content-end");
    }

    // Action buttons will have class form-manager-action for common style
    const actions = actionWrapper?.querySelectorAll(".btn");
    if (actions) {
      forEach(actions, (action: HTMLButtonElement) => this.#renderer.addClass(action, "form-manager-action"));
    }
  }

  private prepareFormName() {
    const parent = this.formIoEl().nativeElement.querySelector(".formio-form");
    let titleEl = this.formIoEl().nativeElement.querySelector(".form-cycle-name");
    if (parent && !titleEl) {
      if (this.title()) {
        titleEl = this.#renderer.createElement("p");
        this.#renderer.setProperty(titleEl, "classList", [
          ...titleEl.classList,
          "form-cycle-name rr-text-primary ms-1 fw-bold mb-0 text-break",
        ]);
        this.#renderer.setProperty(titleEl, "innerHTML", this.title());
        this.#renderer.insertBefore(parent, titleEl, parent.firstElementChild);
      }
    }
  }

  private prepareSubTitle() {
    const parent = this.formIoEl().nativeElement.querySelector(".wizard-page");
    let subTitleEl = this.formIoEl().nativeElement.querySelector(".form-subtitle");
    if (parent && !subTitleEl) {
      subTitleEl = this.#renderer.createElement("h5");
      const subTitle = (
        (this.formIo()?.formio.allPages as [])[this.formIo()?.formio.page as unknown as number] as unknown as {
          component: { subTitle: string };
        }
      ).component.subTitle;
      if (subTitle) {
        this.#renderer.setProperty(subTitleEl, "classList", [...subTitleEl.classList, "mt-2 form-subtitle"]);
        this.#renderer.setProperty(subTitleEl, "innerHTML", subTitle);
        this.#renderer.insertBefore(parent, subTitleEl, parent.firstElementChild);
      }
    }
  }

  private preparePreSubmittedLastPage() {
    const actionWrapper = this.formIoEl().nativeElement.querySelector(".formio-wizard-nav-container");
    const actionNext = actionWrapper?.querySelector(".btn.btn-wizard-nav-next");
    if (!actionNext) {
      this.#renderer.addClass(actionWrapper, "justify-content-center");
      // image checklist
      const checkListImage = this.#renderer.createElement("img");
      this.#renderer.setAttribute(checkListImage, "src", "assets/images/checklist.png");
      this.#renderer.setAttribute(checkListImage, "alt", "check-list");

      // checklist reminder
      const reminder = this.#renderer.createElement("h5");
      this.#renderer.setProperty(reminder, "innerHTML", `Kindly check all of your answers before submission.`);
      this.#renderer.setProperty(reminder, "classList", [...reminder.classList, "rr-text-primary mt-5"]);

      // checklist sub-reminder
      const subReminder = this.#renderer.createElement("p");
      this.#renderer.setProperty(subReminder, "classList", [...subReminder.classList, "mt-3 fw-bold"]);
      this.#renderer.setProperty(subReminder, "innerText", "You can preview your report to check prior to submission.");

      // preview
      const li = this.#renderer.createElement("li");
      this.#renderer.insertBefore(actionWrapper, li, actionWrapper.querySelector("li:nth-child(2)"));
      const previewSubmission = this.#renderer.createElement("button");
      this.#renderer.setProperty(previewSubmission, "innerHTML", "Preview report");
      this.#renderer.setProperty(previewSubmission, "classList", [
        ...previewSubmission.classList,
        "btn btn-primary form-manager-action form-btn-preview",
      ]);
      this.#renderer.appendChild(li, previewSubmission);
      this.#listeners.push(this.#renderer.listen(previewSubmission, "click", () => this.onPreviewReport()));

      const section = this.#renderer.createElement("section");
      this.#renderer.setProperty(section, "classList", [
        ...section.classList,
        "text-center submission-message-wrapper",
      ]);
      this.#renderer.appendChild(section, checkListImage);
      this.#renderer.appendChild(section, reminder);
      this.#renderer.appendChild(section, subReminder);
      const pageWrapper = this.formIoEl().nativeElement.querySelector(".wizard-page");
      this.#renderer.insertBefore(pageWrapper, section, actionWrapper);
    }
  }

  private preparePostSubmittedLastPage() {
    const actionWrapper = this.formIoEl().nativeElement.querySelector(".formio-wizard-nav-container");
    const actionSubmit = actionWrapper?.querySelector(".btn.btn-wizard-nav-submit");

    if (actionSubmit) {
      this.#renderer.addClass(actionWrapper, "justify-content-center");
      this.#renderer.addClass(actionWrapper.querySelector(".btn-wizard-nav-submit"), "d-none");
      const previewReport = actionWrapper.querySelector(".form-btn-preview");
      if (previewReport) {
        this.#renderer.addClass(previewReport.parentElement, "d-none");
      }
      // thank you message
      let sectionWrapper = this.formIoEl().nativeElement.querySelector(".submission-message-wrapper");

      if (sectionWrapper) {
        this.#renderer.addClass(sectionWrapper, "post-submit");
      } else {
        sectionWrapper = this.#renderer.createElement("section");

        this.#renderer.setProperty(sectionWrapper, "className", "text-center submission-message-wrapper");
        const pageWrapper = this.formIoEl().nativeElement.querySelector(".wizard-page");
        this.#renderer.insertBefore(pageWrapper, sectionWrapper, actionWrapper);
      }

      this.#renderer.setProperty(
        sectionWrapper,
        "innerHTML",
        `<h5 class="rr-text-primary mt-5"><i class="pi pi-check-circle me-2"></i>Thank you for your submission.</h5>`,
      );

      // Download Report
      const li = this.#renderer.createElement("li");
      this.#renderer.insertBefore(actionWrapper, li, actionWrapper.querySelector("li:last-child"));
      const downloadReport = this.#renderer.createElement("button");
      this.#renderer.setProperty(downloadReport, "innerHTML", "Download report");
      this.#renderer.setProperty(downloadReport, "classList", [
        ...downloadReport.classList,
        "btn btn-primary form-manager-action",
      ]);
      this.#renderer.appendChild(li, downloadReport);
      this.#listeners.push(this.#renderer.listen(downloadReport, "click", () => this.onDownloadReport()));

      // Go back to Homepage
      const homepageLi = this.#renderer.createElement("li");
      this.#renderer.insertBefore(actionWrapper, homepageLi, actionWrapper.querySelector("li:last-child"));
      const goHome = this.#renderer.createElement("button");
      this.#renderer.setProperty(goHome, "innerHTML", "Go back to Homepage");
      this.#renderer.setProperty(goHome, "classList", [...goHome.classList, "btn btn-primary form-manager-action"]);
      this.#renderer.appendChild(homepageLi, goHome);
      this.#listeners.push(this.#renderer.listen(goHome, "click", () => this.onGoHome()));
    }
  }

  onRender() {
    this.prepareFormName();
    this.prepareSubTitle();
    this.prepareFormActions();
    if (this.submitted()) {
      this.preparePostSubmittedLastPage();
    } else {
      this.preparePreSubmittedLastPage();
    }
  }

  onPreviewReport() {
    this.previewReport.emit();
  }

  onDownloadReport() {
    this.downloadReport.emit();
  }

  onGoHome() {
    this.backToHome.emit();
  }

  onChange(event: object) {
    if (event) {
      const submissionEvent = event as { data: object };
      if (submissionEvent.data) {
        this.submissonEventData.set(submissionEvent.data);
      }
    }
  }

  onSubmit() {
    this.submitted.set(true);
    this.preparePostSubmittedLastPage();
    this.submitForm.emit(this.submissonEventData());
  }

  onCustomEvent(e: object) {
    const event = e as { type: string; component?: { properties: { questionNumber: string | null } } };
    if (event) {
      if (event.type === "showFileUpload") {
        // Persist the current answers before opening the upload dialog. Completing an upload
        // patches the submission in the store, which makes Form.io redraw from persisted data;
        // saving first ensures that redraw restores the user's in-progress answers (and keeps
        // conditional panels expanded) instead of discarding them. Skip when there are no
        // in-progress changes — nothing to save, and the parent treats null as a no-op anyway.
        const currentData = this.submissonEventData();
        if (currentData) {
          this.autoSaveBeforeUpload.emit(currentData);
        }
        if (event.component && event.component.properties) {
          this.showUpload.emit(event.component.properties.questionNumber ?? null);
        } else {
          this.showUpload.emit(null);
        }
      }
    }
  }

  onNext(event: object) {
    if (event) {
      const page = (event as { page: number }).page;
      this.next.emit({
        page,
        submission: this.submissonEventData(),
      });
    }
  }

  onSave() {
    this.save.emit(this.submissonEventData());
  }
}
